// EcosystemHover.tsx
"use client";
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import EgabidIcon from "../Icons/EgabidIcon";
import { motion } from "motion/react";

export const EcosystemHover = ({
  trigger,
}: {
  trigger: React.ReactNode;
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const t = useTranslations('eco-hover');
    const l = useTranslations('ecosystem');

    const appInternal = [
        {
            imgUrl: "/images/icons/only-hyra-scan.svg",
            title: "Hyra Scan", 
            description: t('blockchain-scanner-and-monitor-ensuring-system-transparency-and-security'),
            href: "https://scan.hyra.network/",
        },
        {
            imgUrl: "/images/icons/only-hyra-ai.svg",
            title: "hyra ai", 
            description: t('distributed-ai-infrastructure-using-billions-of-edge-devices-for-efficient-cost-effective-ai-services'),
            href: "https://ai.hyra.network/",
        },
        {
            imgUrl: "/images/icons/only-hyra-zone.svg",
            title: "Hyra Zone", 
            description: t('train-ai-with-the-power-of-decentralized-gpus'),
            href: "https://hyrazone.com/",
        },
        {
            imgUrl: "/images/icons/only-hyra-mega.svg",
            title: "Hyra Mega", 
            description: l('decentralized_marketplace_for_ai_solutions'),
            href: "https://mega.hyra.network/",
        },
    ]

    const appExternal = [
        {
            logo: EgabidIcon,
            title: "Hyra bid", 
            description: t('online-auction-platform-using-blockchain-for-transparent-auctions'),
            href: "https://hyrabid.com/",
        },
        {
            imgUrl: "/images/icons/only-hyra-one.svg",
            title: "Hyra one", 
            description: t('hyra-one-delivers-a-decentralized-financial-experience-secure-transparent-and-breakthrough'),
            href: "/coming-soon",
        },
    ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div 
      ref={containerRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="relative"
    >
      {/* Trigger */}
      <div className="cursor-pointer">
        {trigger}
      </div>

      {/* Dropdown Content */}
      {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3}}
        className="max-lg:hidden absolute left-1/2 top-full pt-4 z-[70] w-[800px] opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100"
        style={{ transform: 'translateX(-50%)' }}
        >
          <div 
            className="grid grid-cols-2 rounded-2xl border-[2px] py-5 border-white bg-[#F9F9F9] shadow-xl backdrop-blur-md  transition-all duration-300 ease-in-out"
            style={{ transform: 'translateX(-150px)' }}
          >
                <div className="max-w-[400px] px-4 py-2 border-r border-gray-200">
                    <p className="mb-2 text-xs font-semibold text-[#646464] uppercase">INTERNAL</p>
                    <ul className="space-y-2 mt-4">
                        {appInternal.map((item, idx) => (
                            <li key={idx}>
                                <Link target="_blank" href={item.href} className="w-full text-sm text-neutral-700 flex items-center justify-start gap-2 p-2 hover:bg-white rounded-xl border border-transparent hover:border-gray-200">
                                        <div className="border rounded-sm flex justify-center items-center w-10 h-10 aspect-square bg-white ">
                                        <Image src={item.imgUrl} width={24} height={24} alt="icon"/>
                                    </div>
                                    <div>
                                        <h3 className="text-sm text-[#202020] uppercase font-medium">{item.title}</h3>
                                        <p className="text-xs text-[#646464] font-light">{item.description}</p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="max-w-[400px] px-4 py-2">
                    <p className="mb-2 text-xs font-semibold text-[#646464] uppercase">EXTERNAL</p>
                    <ul className="space-y-2 mt-4">
                         {appExternal.map((item) => (
                            <li key={item.title}>
                            <Link
                                target="_blank"
                                href={item.href}
                                className="w-full text-sm text-neutral-700 flex items-center justify-start gap-2 p-2 hover:bg-white rounded-xl border border-transparent hover:border-gray-200"
                            >
                                <div className="border rounded-sm flex justify-center items-center w-10 h-10 aspect-square bg-white">
                                {item.logo ? (
                                    <item.logo className="w-6 h-6" aria-label={item.title} />
                                ) : (
                                    <Image src={item.imgUrl!} width={24} height={24} alt={`${item.title} icon`} />
                                )}
                                </div>
                                <div className="text-left">
                                <h3 className="text-sm text-[#202020] uppercase font-medium">{item.title}</h3>
                                <p className="text-xs text-[#646464] font-light">{item.description}</p>
                                </div>
                            </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </motion.div>
      )}
    </div>
  );
};
